import { FireIcon } from '@heroicons/react/24/outline';
import { useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import CoverageShortfallTooltip from 'components/common/coverage-shortfall-tooltip';
import InfoTooltip from 'components/layout/info-tooltip';
import { SystemOptionType } from 'data/types';
import { ReactNode } from 'react';
import { formatPrice } from 'utils/helpers';
import { formatMonthlyOperatingCost } from 'utils/operating-cost-format';
import {
  CoverageSide,
  CoverageSides,
  coverageSidesForSystem
} from './coverage-sides';
import SnowflakeIcon from './snowflake-icon';

// The building blocks of the system overview cards, shared by the comparison
// band, the comparison column, and the ducted detail panel so the same
// figure renders the same way wherever it appears.

type OverviewCardProps = Readonly<{
  title: ReactNode;
  children: ReactNode;
}>;

export function OverviewCard({ title, children }: OverviewCardProps) {
  return (
    <div className='rounded-lg border border-gray-200 bg-white p-3'>
      <div className='mb-2 text-[10px] font-semibold uppercase tracking-wider text-gray-500'>
        {title}
      </div>
      {children}
    </div>
  );
}

export function PerformanceInYourAreaTitle() {
  const zipcode = useAppSelector(
    (state) => state.project.locationData?.zipcode
  );
  return (
    <>
      Performance In Your Area
      {zipcode && ` (${zipcode})`}
    </>
  );
}

type CoverageBlockProps = Readonly<{
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  side: CoverageSide;
  accentClass: string;
  // The compact variant draws its own border for a host that is not already
  // a card, and shrinks the icon to match.
  bordered?: boolean;
}>;

export function CoverageBlock({
  icon: Icon,
  label,
  side,
  accentClass,
  bordered = false
}: CoverageBlockProps) {
  return (
    <div
      className={clsx(
        'rounded-lg p-2 text-center',
        bordered && 'border border-gray-200'
      )}
    >
      <div className='flex items-center justify-center'>
        <CoverageShortfallTooltip
          shortfalls={side.shortfalls ?? []}
          className='flex items-center gap-1'
        >
          <Icon
            className={clsx(bordered ? 'h-5 w-5' : 'h-7 w-7', accentClass)}
          />
          <div className={clsx('text-xl font-bold', accentClass)}>
            {side.value}
          </div>
        </CoverageShortfallTooltip>
      </div>
      <div className='flex items-center justify-center gap-1 text-xs font-semibold uppercase tracking-wider text-gray-900'>
        {label}
        {side.tooltip && (
          <InfoTooltip
            ariaLabel={`What ${label.toLowerCase()} measures`}
            iconClassName='h-3.5 w-3.5 cursor-help text-gray-400 hover:text-gray-600'
          >
            {side.tooltip}
          </InfoTooltip>
        )}
      </div>
      <div className='text-xs text-gray-500'>{side.caption}</div>
      {side.detail && (
        <div className='text-[10px] text-gray-500'>{side.detail}</div>
      )}
    </div>
  );
}

type CoverageGridProps = Readonly<{
  sides: CoverageSides;
  bordered?: boolean;
}>;

export function CoverageGrid({ sides, bordered }: CoverageGridProps) {
  return (
    <div className='grid grid-cols-2 gap-2'>
      <CoverageBlock
        icon={FireIcon}
        label='Heating Coverage'
        side={sides.heating}
        accentClass='text-orange-500'
        bordered={bordered}
      />
      <CoverageBlock
        icon={SnowflakeIcon}
        label='Cooling Coverage'
        side={sides.cooling}
        accentClass='text-blue-500'
        bordered={bordered}
      />
    </div>
  );
}

type PerformanceOverviewCardProps = Readonly<{
  system: SystemOptionType;
}>;

export function PerformanceOverviewCard({
  system
}: PerformanceOverviewCardProps) {
  return (
    <OverviewCard title={<PerformanceInYourAreaTitle />}>
      <CoverageGrid sides={coverageSidesForSystem(system)} />
    </OverviewCard>
  );
}

type CostRowProps = Readonly<{
  label: string;
  sublabel?: ReactNode;
  children: ReactNode;
}>;

export function CostRow({ label, sublabel, children }: CostRowProps) {
  return (
    <div className='flex items-start justify-between gap-2 py-1.5'>
      <div>
        <div className='text-sm text-gray-600'>{label}</div>
        {sublabel != null && sublabel !== '' && (
          <div className='text-xs text-gray-400'>{sublabel}</div>
        )}
      </div>
      <div className='text-right text-lg font-bold text-gray-900'>
        {children}
      </div>
    </div>
  );
}

type OperatingCostAmountProps = Readonly<{
  // Null when no estimate could be made, which reads as the same dash every
  // other unavailable figure on the ducted cards uses.
  annualCost: number | null;
  unit: 'year' | 'month';
}>;

export function OperatingCostAmount({
  annualCost,
  unit
}: OperatingCostAmountProps) {
  if (annualCost === null) {
    return <>—</>;
  }
  const units =
    unit === 'month' ? (['month', 'year'] as const) : ([unit] as const);
  return (
    <div>
      {units.map((displayUnit) => (
        <div key={displayUnit}>
          {displayUnit === 'month'
            ? formatMonthlyOperatingCost(annualCost)
            : formatPrice(annualCost)}{' '}
          <span className='text-sm font-normal text-gray-500'>
            / {displayUnit}
          </span>
        </div>
      ))}
    </div>
  );
}
